export type BrowserType =
  | "chrome"
  | "edge"
  | "firefox"
  | "brave"
  | "opera"
  | "safari"
  | "unknown";

export interface BrowserInfo {
  type: BrowserType;
  name: string;
  extensionUrl?: string;
}

/**
 * Detects the user's browser type based on user agent
 */
export function detectBrowser(): BrowserInfo {
  const userAgent = navigator.userAgent.toLowerCase();
  const vendor = navigator.vendor?.toLowerCase() || "";

  // Check for Brave (must check before Chrome as Brave uses Chrome user agent)
  if (
    (navigator as any).brave &&
    typeof (navigator as any).brave.isBrave === "function"
  ) {
    return {
      type: "brave",
      name: "Brave",
      extensionUrl: getExtensionDownloadUrl("brave"),
    };
  }

  // Check for Opera (must check before Chrome as Opera uses Chrome user agent)
  if (userAgent.includes("opr/") || userAgent.includes("opera")) {
    return {
      type: "opera",
      name: "Opera",
      extensionUrl: getExtensionDownloadUrl("opera"),
    };
  }

  // Check for Edge (must check before Chrome as Edge uses Chrome user agent)
  if (userAgent.includes("edg/") || userAgent.includes("edgios/")) {
    return {
      type: "edge",
      name: "Microsoft Edge",
      extensionUrl: getExtensionDownloadUrl("edge"),
    };
  }

  // Check for Chrome
  if (userAgent.includes("chrome") && vendor.includes("google")) {
    return {
      type: "chrome",
      name: "Google Chrome",
      extensionUrl: getExtensionDownloadUrl("chrome"),
    };
  }

  // Check for Firefox
  if (userAgent.includes("firefox") || userAgent.includes("fxios")) {
    return {
      type: "firefox",
      name: "Mozilla Firefox",
      extensionUrl: getExtensionDownloadUrl("firefox"),
    };
  }

  // Check for Safari (must check after Chrome as Safari user agent includes 'chrome')
  if (
    userAgent.includes("safari") &&
    !userAgent.includes("chrome") &&
    vendor.includes("apple")
  ) {
    return {
      type: "safari",
      name: "Safari",
      extensionUrl: getExtensionDownloadUrl("safari"),
    };
  }

  // Unknown browser
  return {
    type: "unknown",
    name: "Unknown Browser",
  };
}

/** Chrome Web Store extension URL used for Chromium browsers. */
const CHROME_WEB_STORE_EXTENSION_URL =
  "https://chromewebstore.google.com/detail/kairos-job-application-br/colcihdaklmclebfngelcpokpopbhpfp";

/**
 * True if the "Get Extension" banner and install link should be shown for this browser.
 * Chromium browsers (Chrome, Edge, Brave, Opera): extension available via Chrome Web Store.
 * Firefox/Safari/unknown: no extension link.
 */
export function isExtensionAvailableForBrowser(browser: BrowserType): boolean {
  return (
    browser === "chrome" ||
    browser === "edge" ||
    browser === "brave" ||
    browser === "opera"
  );
}

/**
 * Returns the extension download URL for the given browser type.
 * Chromium browsers use the same Chrome Web Store URL.
 */
export function getExtensionDownloadUrl(
  browser: BrowserType,
): string | undefined {
  switch (browser) {
    case "chrome":
    case "edge":
    case "brave":
    case "opera":
      return CHROME_WEB_STORE_EXTENSION_URL;
    case "firefox":
      return undefined; // Not published yet
    case "safari":
    case "unknown":
    default:
      return undefined;
  }
}
